import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface UatPlanView {
  readonly uatPlanId: string;
  readonly uatCode: string;
  readonly title: string;
  readonly releaseRef: string | null;
  readonly businessProcess: string | null;
  readonly scope: string | null;
  readonly scheduleStart: string | null;
  readonly scheduleEnd: string | null;
  readonly status: string;
  readonly goLiveRecommendation: string;
}

interface SignoffRow {
  readonly uatSignoffId: string;
  readonly signoffCode: string;
  readonly businessOwnerId: string;
  readonly signoffDate: string | null;
  readonly decision: string;
  readonly status: string;
}

/**
 * GR_M31-M35 §L34.05 / §M — UAT detail viewer (`/admin/qms/uat/:id`). Renders a
 * single UAT plan (T456) with its business scope and schedule, plus the recorded
 * business sign-offs (§M L34.05 uat.signoff_user → the sign-off's
 * business_owner_id, T457) and their go / no-go decisions, and drives the plan
 * lifecycle — start, complete, sign-off — each wired to the guarded /v1/qms/uat
 * write API (sign-off carries the segregated UAT_APPROVE permission, §30 SoD), so
 * a release manager can action acceptance without leaving the detail view.
 */
export function UatDetailPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [plan, setPlan] = useState<UatPlanView | null>(null);
  const [signoffs, setSignoffs] = useState<SignoffRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [decision, setDecision] = useState('GO');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m34.uatDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: UatPlanView }>(`/v1/qms/uat/${id}`, { token })
      .then(async (r) => {
        if (!alive) return;
        setPlan(r.data);
        setError(null);
        try {
          const s = await apiRequest<{ data: SignoffRow[] }>(
            `/v1/qms/uat/${id}/signoffs`, { token });
          if (alive) setSignoffs(s.data ?? []);
        } catch {
          if (alive) setSignoffs([]);
        }
      })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m34.uatDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const act = async (path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/uat/${id}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.uatDetail.notice', { label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  if (loading) return <main aria-label="uat-detail"><p>{t('m34.common.loading')}</p></main>;
  if (error) return <main aria-label="uat-detail"><p role="alert">{error}</p></main>;
  if (!plan) return <main aria-label="uat-detail"><p role="alert">{t('m34.uatDetail.notFound')}</p></main>;

  return (
    <main aria-label="uat-detail">
      <h1>{plan.title}</h1>
      <dl>
        <dt>{t('m34.common.code')}</dt><dd aria-label="uat-code">{plan.uatCode}</dd>
        <dt>{t('m34.common.release')}</dt><dd aria-label="uat-release">{plan.releaseRef ?? '—'}</dd>
        <dt>{t('m34.uatDetail.dtProcess')}</dt><dd aria-label="uat-process">{plan.businessProcess ?? '—'}</dd>
        <dt>{t('m34.uatDetail.dtScheduleStart')}</dt><dd aria-label="uat-schedule-start">{plan.scheduleStart ?? '—'}</dd>
        <dt>{t('m34.uatDetail.dtScheduleEnd')}</dt><dd aria-label="uat-schedule-end">{plan.scheduleEnd ?? '—'}</dd>
        <dt>{t('m34.uatDetail.dtGoLive')}</dt><dd aria-label="uat-go-live">{plan.goLiveRecommendation}</dd>
        <dt>{t('m34.common.status')}</dt><dd aria-label="uat-status">{plan.status}</dd>
      </dl>

      {plan.scope && <p aria-label="uat-scope">{plan.scope}</p>}

      <section aria-label="uat-lifecycle">
        <h2>{t('m34.uatDetail.lifecycle')}</h2>
        {plan.status === 'PLANNED' && (
          <Button variant="secondary" type="button" aria-label="start-uat" onClick={() => void act('start', t('m34.uatDetail.act.started'), 'M34_UAT_MANAGE')}>{t('m34.uatDetail.btnStart')}</Button>
        )}
        {plan.status === 'IN_PROGRESS' && (
          <Button variant="secondary" type="button" aria-label="complete-uat" onClick={() => void act('complete', t('m34.uatDetail.act.completed'), 'M34_UAT_MANAGE')}>{t('m34.uatDetail.btnComplete')}</Button>
        )}
        {plan.status === 'COMPLETED' && (
          <>
            <select aria-label="signoff-decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
              {['GO', 'CONDITIONAL_GO', 'NO_GO'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button variant="secondary" type="button" aria-label="signoff-uat" onClick={() => void act('signoff', t('m34.uatDetail.act.signedOff'), 'M34_UAT_APPROVE', { decision })}>{t('m34.uatDetail.btnSignOff')}</Button>
          </>
        )}
        {actionError && <p role="alert" aria-label="uat-action-error">{actionError}</p>}
        {notice && <p aria-label="uat-action-notice">{notice}</p>}
      </section>

      <section aria-label="uat-signoffs">
        <h2>{t('m34.uatDetail.signoffsHeading')}</h2>
        {signoffs.length > 0 ? (
          <DataTable<SignoffRow>
            ariaLabel="uat-signoff-table"
            rowKey={(s) => s.uatSignoffId}
            rows={signoffs}
            rowDataAttrs={(s) => ({ 'data-decision': s.decision })}
            columns={[
              { key: 'signoffCode', header: t('m34.uatDetail.colSignoff') },
              { key: 'businessOwnerId', header: t('m34.uatDetail.colOwner'), cellAriaLabel: (s) => `signoff-owner-${s.uatSignoffId}` },
              { key: 'decision', header: t('m34.uatDetail.colDecision') },
              { key: 'signoffDate', header: t('m34.uatDetail.colDate'), render: (s) => day(s.signoffDate) },
            ]}
          />
        ) : (
          <p aria-label="uat-no-signoffs">{t('m34.uatDetail.noSignoffs')}</p>
        )}
      </section>
    </main>
  );
}
